import React, { useCallback, useContext } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { SCREEN_SIZE } from "src/stores/UIStore";
import { observer } from "mobx-react";
import { UI } from "@wwimmo/ui";
import { ColorStyle } from "src/utils/Colors";
import { formatDate } from "src/stores/krediflow/Utils";
import { INVOICE_PROPERTY, INVOICE_STATE, LOADING_TYPE } from "src/stores/krediflow/InvoiceEnums";
import { formatCurrency } from "src/utils/Currency";
import { CreditorFormFields } from "src/screens/creditors/creditor/details/form/fields/CreditorFormFields";
import { AiRealestateHint } from "src/screens/creditors/creditor/details/form/fields/AiRealestateHint";
import { InvoiceDetailFormFields } from "src/screens/creditors/creditor/details/form/fields/InvoiceDetailFormFields";
import { NetworkConfig } from "src/network/NetworkConfig";
import { RealestateFormField } from "src/screens/creditors/creditor/details/form/fields/RealestateFormField";
import { UnitFormField } from "src/screens/creditors/creditor/details/form/fields/UnitFormField";
import { TicketFormField } from "src/screens/creditors/creditor/details/form/fields/TicketFormField";
import { OrderFormField } from "src/screens/creditors/creditor/details/form/fields/OrderFormField";
import { PayoutAccountIT2Field } from "src/screens/creditors/creditor/details/accountings/accounting/PayoutAccountFieldIT2";
import { ErpType } from "src/network/User";
import styles from "./InvoiceFormDetails.module.css";
import { formatDisplayId } from "src/utils/DisplayId";

const InvoiceFormDetailsBase = () => {
    const { t } = useTranslation();
    const { uiStore, invoiceStore, invoiceFormStore, authStore } = useContext(RootStoreContext);

    const screenSize =
        uiStore.currentScreenSize <= SCREEN_SIZE.MOBILE_L ? uiStore.currentScreenSize : uiStore.invoicePaneScreenSize;

    const onChangeInvoiceField = useCallback(
        (invoiceProperty: INVOICE_PROPERTY) => (e: any) => {
            if (invoiceStore.currentInvoice) {
                let newPropertyValue: string | number = e.target.value;

                if (invoiceProperty === INVOICE_PROPERTY.INVOICE_TYPE) {
                    newPropertyValue = Number(e.target.value);
                }

                invoiceStore.currentInvoice.updateProperty(invoiceProperty, newPropertyValue);
            }
        },
        [invoiceStore.currentInvoice]
    );

    const editForm = useCallback(
        (e: any) => {
            e.preventDefault();
            invoiceStore.setIsEditing(true);
        },
        [invoiceStore]
    );

    const saveFormButtonLabel = (
        <div className="d-flex align-items-center">
            <div>{t("labels.save")}</div>
            <UI.RotatingSpinner
                className={`${styles.FromSaveButtonSpinner} ${
                    invoiceStore.loadingType !== LOADING_TYPE.SAVING_INVOICE ? "d-none" : ""
                }`}
                noLogo={true}
                size={20}
            />
        </div>
    );

    const saveFormButton =
        screenSize < SCREEN_SIZE.TABLET ? (
            <>
                {invoiceStore.loadingType !== LOADING_TYPE.NONE ? (
                    <UI.RotatingSpinner noLogo={true} size={30} />
                ) : (
                    <UI.Icon
                        onClick={
                            invoiceStore.currentInvoice?.isNewInvoice && !invoiceStore.currentInvoice.isFileSelected
                                ? invoiceFormStore.toggleDisplaySaveInvoiceWithoutDocumentModal
                                : invoiceFormStore.saveForm(true)
                        }
                        icon={UI.SVGIcon.Save}
                        color={ColorStyle("primary")}
                        size={"medium"}
                    />
                )}
            </>
        ) : (
            <UI.Button
                onClick={
                    invoiceStore.currentInvoice?.isNewInvoice && !invoiceStore.currentInvoice.isFileSelected
                        ? invoiceFormStore.toggleDisplaySaveInvoiceWithoutDocumentModal
                        : invoiceFormStore.saveForm(true)
                }
                className={styles.SaveButton}
                label={saveFormButtonLabel}
                disabled={invoiceStore.loadingType !== LOADING_TYPE.NONE}
            />
        );

    const editFormButton =
        screenSize < SCREEN_SIZE.TABLET ? (
            <UI.Icon
                className={styles.EditIcon}
                onClick={invoiceStore.isInvoiceLocked ? undefined : editForm}
                icon={UI.SVGIcon.Edit}
                color={
                    invoiceStore.currentInvoice?.state === INVOICE_STATE.CANCELLED
                        ? ColorStyle("grey")
                        : ColorStyle("primary")
                }
                size={"medium"}
            />
        ) : (
            <UI.Button
                onClick={editForm}
                className={`${styles.EditButton} ${invoiceStore.isInvoiceLocked ? styles.Disabled : ""}`}
                variant={"outline-primary"}
                label={t("labels.edit")}
                disabled={invoiceStore.isInvoiceLocked}
            />
        );

    const isNewInvoice = invoiceStore.currentInvoice?.isNewInvoice;

    const formattedInvoiceNumber = formatDisplayId(invoiceStore.currentInvoice?.number);

    const creditorName =
        (invoiceStore.currentInvoice &&
            invoiceStore.currentInvoice.creditor &&
            invoiceStore.currentInvoice.creditor.name) ??
        "";

    const formattedAmount = invoiceStore.currentInvoice?.amount
        ? `CHF ${formatCurrency(invoiceStore.currentInvoice?.amount)}`
        : "";

    let realestatePlaceholderIconSize = 170;

    if (screenSize <= SCREEN_SIZE.MOBILE_L) {
        realestatePlaceholderIconSize = 100;
    } else if (screenSize <= SCREEN_SIZE.LAPTOP) {
        realestatePlaceholderIconSize = 125;
    }

    return (
        <UI.Form className={styles.InvoiceForm} id={"invoice-form"}>
            {/* Invoice-Number - Keditor-Name, Amount / Creation-Date*/}
            <UI.Row className={`mt-2 ${styles.TitleRow}`}>
                <UI.Col
                    md={9}
                    className={`${uiStore.currentScreenSize <= SCREEN_SIZE.MOBILE_L ? "order-2" : ""} ${
                        isNewInvoice ? "d-none" : ""
                    } ${uiStore.isMobile && !invoiceStore.isEditing ? "mb-3" : ""}`}
                >
                    <div>
                        <div className={styles.InvoiceNumber}>
                            {`${formattedInvoiceNumber} - ${creditorName}, ${formattedAmount}`}
                        </div>
                        <div className={styles.CreationDate}>
                            {invoiceStore.currentInvoice?.date &&
                                formatDate(invoiceStore.currentInvoice.date, false, true)}
                        </div>
                    </div>
                </UI.Col>

                <UI.Col
                    md={3}
                    className={`d-flex justify-content-end ${styles.EditColumn} ${
                        uiStore.currentScreenSize <= SCREEN_SIZE.MOBILE_L ? "order-1 mb-3" : ""
                    } ${invoiceStore.isEditing && invoiceStore.currentInvoice?.isNewInvoice ? "ml-auto" : ""}`}
                >
                    {invoiceStore.isEditing ? saveFormButton : editFormButton}
                </UI.Col>
            </UI.Row>

            {/* Status / Invoice-Typ */}
            <UI.Row>
                <div className={screenSize >= SCREEN_SIZE.LAPTOP ? "col-4" : "col-6"}>
                    <UI.Input
                        label={t("screens.kredi_flow.list.state").toString()}
                        type="text"
                        autoComplete="off"
                        disabled={true}
                        value={
                            invoiceStore.currentInvoice && invoiceStore.currentInvoice.isProcessing
                                ? t("screens.kredi_flow.state.workflow_processing").toString()
                                : invoiceStore.currentInvoice && invoiceStore.currentInvoice.state
                                ? t(`screens.kredi_flow.state.${String(invoiceStore.currentInvoice.state)}`).toString()
                                : ""
                        }
                    />
                </div>
                <div className={screenSize >= SCREEN_SIZE.LAPTOP ? "col-4" : "col-6"}>
                    <UI.Input
                        label={t("screens.kredi_flow.list.type").toString()}
                        type="text"
                        as="select"
                        onChange={onChangeInvoiceField(INVOICE_PROPERTY.INVOICE_TYPE)}
                        className={`${styles.FormFieldSelect} ${invoiceStore.isEditing ? "" : styles.ReadOnly}`}
                        disabled={
                            !invoiceStore.isEditing ||
                            !invoiceStore.currentInvoice?.isInvoiceStateAllowingToChangeInvoiceType
                        }
                        value={String(invoiceStore.currentInvoice?.invoiceType)}
                    >
                        {invoiceStore.invoiceTypes.map((invoiceType) => {
                            return (
                                <option key={invoiceType.type} value={invoiceType.type ?? ""}>
                                    {invoiceType.name}
                                </option>
                            );
                        })}
                    </UI.Input>
                </div>
            </UI.Row>

            {/* Creditor */}
            <div className="mt-3">
                <span className={styles.FormSectionTitle}>
                    {t("screens.kredi_flow.form.creditor.title").toString()}
                </span>
            </div>
            <CreditorFormFields />
            <hr />

            {/* Rechnungsdetails */}
            <div className="mt-3">
                <span className={styles.FormSectionTitle}>{t("screens.kredi_flow.invoice").toString()}</span>
            </div>
            <InvoiceDetailFormFields />

            <hr />

            {/* Objekt / Auftrag */}
            <UI.Row className="mt-3">
                {/* Objekt */}
                <div className={screenSize >= SCREEN_SIZE.LAPTOP ? "col-7" : "col-12"}>
                    <UI.Row className="mb-3">
                        <UI.Col>
                            <span className={styles.FormSectionTitle}>{t("screens.unit.object")}</span>
                        </UI.Col>
                    </UI.Row>
                    <UI.Row>
                        <UI.Col lg={4}>
                            <div className="d-flex align-items-center justify-content-center h-100">
                                {invoiceStore.currentInvoice?.realestateThumbnailFileId ? (
                                    <UI.Thumbnail
                                        src={
                                            NetworkConfig.openThumbnailUrl +
                                            invoiceStore.currentInvoice?.realestateThumbnailFileId
                                        }
                                        className={styles.RealestateThumbnail}
                                    />
                                ) : (
                                    <div
                                        className={`${styles.RealestatePlaceholderThumbnailContainer} ${
                                            screenSize <= SCREEN_SIZE.MOBILE_L
                                                ? styles.Small
                                                : screenSize <= SCREEN_SIZE.LAPTOP
                                                ? styles.Medium
                                                : styles.Large
                                        }`}
                                    >
                                        <UI.Icon
                                            icon={UI.SVGIcon.Consolidated}
                                            size={realestatePlaceholderIconSize}
                                            color={ColorStyle("grey")}
                                            backgroundColor="#D2D5D9"
                                        />
                                    </div>
                                )}
                            </div>
                        </UI.Col>
                        <UI.Col lg={8}>
                            <UI.Row>
                                <UI.Col lg={12} className={styles.RealestateFormFieldContainer}>
                                    <RealestateFormField />
                                </UI.Col>
                                {/* AI hint sits between Liegenschaft and Objekt as its own row so
                                    it pushes the Unit field down naturally when it renders. The
                                    matching mirror is dropped into the Auftrag column below the
                                    Ticket field so both columns grow in lockstep. */}
                                <UI.Col lg={12}>
                                    <AiRealestateHint />
                                </UI.Col>
                                <UI.Col
                                    lg={12}
                                    className={`${uiStore.currentScreenSize <= SCREEN_SIZE.MOBILE_L ? "mt-4" : ""}`}
                                >
                                    <UnitFormField />
                                </UI.Col>
                                {/* Zahlstelle Auszahlung */}
                                {authStore.user?.erpType === ErpType.IT2 ? (
                                    <UI.Col
                                        lg={12}
                                        className={`${
                                            uiStore.currentScreenSize <= SCREEN_SIZE.MOBILE_L ? "mt-4" : ""
                                        } ${invoiceStore.currentInvoice?.canSeePayoutAccountField() ? "" : "d-none"}`}
                                    >
                                        <PayoutAccountIT2Field />
                                    </UI.Col>
                                ) : (
                                    <></>
                                )}
                            </UI.Row>
                        </UI.Col>
                    </UI.Row>
                </div>

                {/* Auftrag */}
                <div className={screenSize >= SCREEN_SIZE.LAPTOP ? "col-5" : "col-12"}>
                    <UI.Row className={`${screenSize < SCREEN_SIZE.LAPTOP ? "mt-4 mb-1" : "mt-0 mb-3"}`}>
                        <span
                            className={`${styles.FormSectionTitle} ${
                                invoiceStore.isEditing || screenSize < SCREEN_SIZE.LAPTOP ? "col" : ""
                            }`}
                        >
                            {t("screens.orders.order")}
                        </span>
                    </UI.Row>
                    <UI.Row>
                        <UI.Col
                            lg={12}
                            className={`${styles.InvoiceFormFieldContainer} ${
                                !invoiceStore.isEditing && screenSize >= SCREEN_SIZE.LAPTOP ? styles.ReadOnlyMode : ""
                            }`}
                        >
                            <TicketFormField />
                        </UI.Col>
                        {/* Invisible mirror of the Liegenschaft AI hint. Renders identical DOM
                            (same max-width, so text wraps identically) but stays hidden — reserves
                            the exact same vertical row space so the Auftrag field stays aligned
                            with the Objekt field on the left. */}
                        <UI.Col lg={12}>
                            <AiRealestateHint mirrorOnly />
                        </UI.Col>
                        <UI.Col
                            lg={12}
                            className={`${styles.OrderFormFieldContainer} ${
                                !invoiceStore.isEditing && screenSize >= SCREEN_SIZE.LAPTOP ? styles.ReadOnlyMode : ""
                            }`}
                        >
                            <OrderFormField />
                        </UI.Col>
                    </UI.Row>
                </div>
            </UI.Row>
        </UI.Form>
    );
};

export const InvoiceFormDetails = Sentry.withProfiler(observer(InvoiceFormDetailsBase));
